// src/services/api.js - FIXED VERSION (trailing slashes + redirect follow)
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000,
  withCredentials: false,
  // ✅ FIX 1: Follow redirects and keep method (fixes 308)
  maxRedirects: 5,
});

// ─── Request Interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response Interceptor ─────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      return Promise.reject({
        success: false,
        message: 'Network error. Please check your internet connection.',
        isNetworkError: true,
        originalError: error,
      });
    }

    const { status, data } = error.response;

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('refreshToken');
      if (
        !window.location.pathname.includes('/login') &&
        !window.location.pathname.includes('/register') &&
        !window.location.pathname.includes('/auth/callback') &&
        !window.location.pathname.includes('/auth/google/callback')
      ) {
        setTimeout(() => { window.location.href = '/login?session=expired'; }, 100);
      }
    }

    if (status === 403) return Promise.reject({ success: false, message: data.message || 'Access forbidden.', isForbidden: true, status, data });
    if (status === 404) return Promise.reject({ success: false, message: data.message || 'Resource not found.', isNotFound: true, status, data });
    if (status >= 500) return Promise.reject({ success: false, message: 'Server error. Please try again later.', isServerError: true, status, data });
    if (status === 422) return Promise.reject({ success: false, message: data.message || 'Validation failed.', errors: data.errors, isValidationError: true, status, data });

    return Promise.reject({ success: false, message: data.message || 'An error occurred.', status, data, originalError: error });
  }
);

// ==================== AUTH API ====================
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  verify: () => api.get('/auth/verify'),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  googleLogin: () => api.get('/auth/oauth/google/login'),
  googleCallback: (data) => api.post('/auth/oauth/google/callback', data),
  googleProcessCode: (data) => api.post('/auth/oauth/google/process-code', data),
  googleExchange: (data) => api.post('/auth/oauth/google/exchange', data),
  googleUpdateRole: (data) => api.post('/auth/oauth/google/update-role', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  updateUserType: (data) => api.post('/auth/update-user-type', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  updateFullProfile: (data) => api.put('/user/profile/full', data),
  checkEmail: (email) => api.get(`/auth/check-email/${email}`),
};

// ==================== USER API ====================
export const userAPI = {
  getProfile: () => api.get('/user/profile'),
  updateProfile: (data) => api.put('/user/profile', data),
  updateAvatar: (formData) => api.post('/user/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getNotifications: () => api.get('/user/notifications'),
  markAllAsRead: () => api.put('/user/notifications/mark-all-read'),
  getDashboardStats: () => api.get('/user/dashboard-stats'),
  // ✅ Fixed: use /buyer/ prefix which is the registered blueprint
  getMyInvoices: () => api.get('/buyer/invoices'),
  getInvoiceById: (id) => api.get(`/buyer/invoices/${id}`),
  downloadInvoice: (id) => api.get(`/buyer/invoices/${id}/download`, { responseType: 'blob' }),
  // ✅ FIXED: accepts FormData for payment proof file upload
  makePayment: (id, data) => {
    if (data instanceof FormData) {
      return api.post(`/buyer/invoices/${id}/payment`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post(`/buyer/invoices/${id}/payment`, data);
  },
  getPaymentHistory: (id) => api.get(`/buyer/invoices/${id}/payments`),
  getInvoiceStats: () => api.get('/buyer/invoices/stats'),
};

// ==================== FRUITS API ====================
// ✅ FIX 2: All endpoints use trailing slash to avoid 308 redirects
export const fruitsAPI = {
  // Public endpoints — trailing slash matches Flask route
  getAll: (params = {}) => api.get('/fruits/', { params }),
  getById: (id) => api.get(`/fruits/${id}`),
  getCategories: () => api.get('/fruits/categories'),
  getByCategory: (category) => api.get(`/fruits/category/${category}`),
  search: (query) => api.get(`/fruits/search?q=${query}`),
  getFeatured: () => api.get('/fruits/featured'),

  // Buyer endpoints
  createEnquiry: (fruitId, data) => api.post(`/fruits/${fruitId}/enquire`, data),
  getFavorites: () => api.get('/fruits/favorites'),
  toggleFavorite: (fruitId) => api.post(`/fruits/${fruitId}/favorite`),

  // Admin endpoints
  create: (formData) => api.post('/admin/fruits/create', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, formData) => api.put(`/admin/fruits/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id) => api.delete(`/admin/fruits/${id}`),
  adminGetAll: () => api.get('/admin/fruits/'),
};

// ==================== ENQUIRY API ====================
export const enquiryAPI = {
  create: (data) => api.post('/enquiry/create', data),
  getUserEnquiries: () => api.get('/enquiry/user'),
  getById: (id) => api.get(`/enquiry/${id}`),
  cancelEnquiry: (id) => api.post(`/enquiry/${id}/cancel`),
  updateStatus: (id, data) => api.put(`/enquiry/${id}/status`, data),
  getChats: (enquiryId) => api.get(`/enquiry/${enquiryId}/chats`),
  sendMessage: (enquiryId, data) => api.post(`/enquiry/${enquiryId}/message`, data),
  getAllEnquiries: () => api.get('/admin/enquiries'),
  assignEnquiry: (id, data) => api.put(`/admin/enquiries/${id}/assign`, data),
  getSellerEnquiries: () => api.get('/seller/enquiries'),
};

// ==================== CHAT API ====================
export const chatAPI = {
  getMessages: (enquiryId) => api.get(`/chat/${enquiryId}`),
  sendMessage: (enquiryId, data) => api.post(`/chat/${enquiryId}/send`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  sendFile: (enquiryId, formData) => api.post(`/chat/${enquiryId}/send`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  markAsRead: (messageId) => api.put(`/chat/message/${messageId}/read`),
  getUnreadCount: () => api.get('/chat/unread-count'),
};

// ==================== PAYMENT API ====================
export const paymentAPI = {
  getPayments:  (enquiryId) => api.get(`/payment/enquiry/${enquiryId}`),
  submit:       (formData)  => api.post('/payment/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMethods:   ()          => api.get('/payment/methods'),
  getHistory:   ()          => api.get('/payment/history'),
  verifyPayment:(id, data)  => api.post(`/payment/${id}/verify`, data),
  // Split payment stages
  getTerms:    (enquiryId)  => api.get(`/payment/terms/${enquiryId}`),
  submitStage: (formData)   => api.post('/payment/submit-stage', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ==================== SHIPPING API ====================
export const shippingAPI = {
  getShipment:       (enquiryId)       => api.get(`/shipping/enquiry/${enquiryId}`),
  updateShipment:    (data)            => api.post('/shipping/update', data),
  trackShipment:     (trackingNumber)  => api.get(`/shipping/track/${trackingNumber}`),
  getMethods:        ()                => api.get('/shipping/methods'),
  getHistory:        ()                => api.get('/shipping/history'),
  getBuyerShipments: ()                => api.get('/buyer/shipments'),
};

// ==================== VERIFICATION API ====================
export const verificationAPI = {
  submit: (formData) => api.post('/verification/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getStatus: () => api.get('/verification/status'),
  getDocuments: () => api.get('/verification/documents'),
  getAllVerifications: () => api.get('/admin/verifications'),
  verifyUser: (id, data) => api.post(`/admin/verifications/${id}/verify`, data),
};

// ==================== ADMIN API ====================
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getAllUsers: () => api.get('/admin/users'),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  changeUserRole: (id, role) => api.post(`/admin/users/${id}/role`, { role }),
  createFruit: (data) => fruitsAPI.create(data),
  getFruits: () => api.get('/admin/fruits/'),
  getAllFruits: () => api.get('/admin/fruits/'),
  updateFruit: (id, data) => fruitsAPI.update(id, data),
  deleteFruit: (id) => fruitsAPI.delete(id),
  getAllEnquiries: () => api.get('/admin/enquiries'),
  getEnquiryById: (id) => api.get(`/admin/enquiries/${id}`),
  updateEnquiryStatus: (id, data) => api.put(`/admin/enquiries/${id}/status`, data),
  assignEnquiry: (id, data) => api.put(`/admin/enquiries/${id}/assign`, data),
  getAllPayments: () => api.get('/admin/payments'),
  getPaymentById: (id) => api.get(`/admin/payments/${id}`),
  verifyPayment: (id, data) => api.post(`/admin/payments/${id}/verify`, data),
  rejectPayment: (id, data) => api.post(`/admin/payments/${id}/reject`, data),
  getAllVerifications: () => api.get('/admin/verifications'),
  verifyUser: (id, data) => api.post(`/admin/verifications/${id}/verify`, data),
  // ✅ FIXED: verifyBuyer and rejectVerification now exist (Verifications.jsx was calling missing methods)
  verifyBuyer: (data) => api.post('/admin/verifications/verify', data),
  rejectVerification: (data) => api.post('/admin/verifications/reject', data),
  getVerificationDocument: (id) => `${API_BASE_URL}/admin/verifications/${id}/document`,
  getAllShipments: () => api.get('/admin/shipments'),
  createShipment: (data) => {
    if (data instanceof FormData) {
      return api.post('/admin/shipments', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/admin/shipments', data);
  },
  updateShipmentStatus: (id, data) => api.put(`/admin/shipments/${id}/status`, data),
  updateFullShipment: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/admin/shipments/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.put(`/admin/shipments/${id}`, data);
  },
  deleteShipment: (id) => api.delete(`/admin/shipments/${id}`),
  updateShipment: (data) => shippingAPI.updateShipment(data),
  getAllInvoices: () => api.get('/admin/invoices'),
  createInvoice: (data) => api.post('/admin/invoices', data),
  uploadInvoice: (formData) => api.post('/admin/invoices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getInvoiceById: (id) => api.get(`/admin/invoices/${id}`),
  getInvoiceByEnquiry: (enquiryId) => api.get(`/admin/invoices/enquiry/${enquiryId}`),
  updateInvoice: (id, data) => api.put(`/admin/invoices/${id}`, data),
  updateInvoiceStatus: (id, data) => api.put(`/admin/invoices/${id}/status`, data),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  downloadInvoice: (id) => api.get(`/admin/invoices/${id}/download`, { responseType: 'blob' }),
  sendInvoiceToBuyer: (invoiceId) => api.post(`/admin/invoices/${invoiceId}/send`),
  getInvoiceStats: () => api.get('/admin/invoices/stats'),
  getInvoiceReports: (params = {}) => api.get('/admin/invoices/reports', { params }),
  // ✅ Product visibility toggle
  toggleFruitActive: (id) => api.put(`/admin/fruits/${id}/toggle-active`),
  // ✅ User activation/deactivation toggle
  toggleUserStatus: (id) => api.put(`/admin/users/${id}/toggle-active`),
  // ✅ Notify buyer (returns whatsapp_link + buyer email)
  notifyBuyer: (data) => api.post('/admin/notify-buyer', data),
  // Works for any user (seller, buyer, admin) — backend only needs user id
  notifyUser:   (data) => api.post('/admin/notify-buyer', data),
  // Seller purchase-order notes + payment proof forwarding
  setSellerNotes:   (enquiryId, data) => api.put(`/admin/enquiries/${enquiryId}/seller-notes`, data),
  getPaymentProofs: (enquiryId)       => api.get(`/admin/enquiries/${enquiryId}/payment-proofs`),
  // Payment terms per user (admin assigns)
  getUserPaymentTerms: (userId)         => api.get(`/admin/users/${userId}/payment-terms`),
  setUserPaymentTerms: (userId, terms)  => api.put(`/admin/users/${userId}/payment-terms`, { payment_terms: terms }),
  // Seller assignment per enquiry
  getSellers:           ()               => api.get('/admin/sellers'),
  assignEnquirySeller:  (id, sellerId)   => api.put(`/admin/enquiries/${id}/assign-seller`, { seller_id: sellerId }),
  // Payment terms per enquiry
  setEnquiryPaymentTerms: (id, terms)   => api.put(`/payment/enquiry/${id}/set-terms`, { payment_terms: terms }),
  // Generic methods for AdminFruits page
  get: (url) => api.get(url),
  post: (url, data) => api.post(url, data),
  put: (url, data) => api.put(url, data),
  delete: (url) => api.delete(url),
};

// ==================== SELLER API ====================
export const sellerAPI = {
  getProfile: () => api.get('/seller/profile'),
  updateProfile: (data) => api.put('/seller/profile', data),
  getMyFruits: () => api.get('/seller/fruits'),
  addFruit: (formData) => api.post('/seller/fruits', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateFruit: (id, formData) => api.put(`/seller/fruits/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFruit: (id) => api.delete(`/seller/fruits/${id}`),
  getMyEnquiries: () => api.get('/seller/enquiries'),
  getMyOrders:    () => api.get('/seller/orders'),          // enhanced: includes payments+shipment
  getMyShipments: () => api.get('/seller/shipments'),       // dedicated shipment tracking
  updateEnquiryStatus: (id, data) => api.put(`/seller/enquiries/${id}/status`, data),
  getMyInvoices: () => api.get('/seller/invoices'),
  getInvoiceById: (id) => api.get(`/seller/invoices/${id}`),
  createInvoice: (data) => api.post('/seller/invoices', data),
  uploadInvoice: (formData) => api.post('/seller/invoices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateInvoice: (id, data) => api.put(`/seller/invoices/${id}`, data),
  updateInvoiceStatus: (id, data) => api.put(`/seller/invoices/${id}/status`, data),
  sendInvoiceEmail: (id, data) => api.post(`/seller/invoices/${id}/send`, data),
  downloadInvoice: (id) => api.get(`/seller/invoices/${id}/download`, { responseType: 'blob' }),
  getInvoiceStats: () => api.get('/seller/invoices/stats'),
  getDashboardStats: () => api.get('/seller/dashboard-stats'),
};

// ==================== BUYER API ====================
export const buyerAPI = {
  getProfile: () => api.get('/buyer/profile'),
  updateProfile: (data) => api.put('/buyer/profile', data),
  getDashboardStats: () => api.get('/buyer/dashboard-stats'),
  getMyInvoices: () => api.get('/buyer/invoices'),
  getInvoiceById: (id) => api.get(`/buyer/invoices/${id}`),
  downloadInvoice: (id) => api.get(`/buyer/invoices/${id}/download`, { responseType: 'blob' }),
  makePayment: (id, data) => api.post(`/buyer/invoices/${id}/payment`, data),
  getPaymentHistory: (id) => api.get(`/buyer/invoices/${id}/payments`),
  getInvoiceStats: () => api.get('/buyer/invoices/stats'),
  getVerificationStatus: () => verificationAPI.getStatus(),
  submitVerification: (formData) => verificationAPI.submit(formData),
};

// ==================== INVOICE API ====================
// ✅ FIX: invoiceAPI re-added (was missing, caused Buyer/Dashboard.jsx crash)
export const invoiceAPI = {
  // Common
  getInvoiceById: (id) => api.get(`/invoices/${id}`),
  downloadInvoice: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),

  // Buyer
  getMyInvoices: () => api.get('/buyer/invoices'),
  makePayment: (id, data) => api.post(`/buyer/invoices/${id}/payment`, data),
  getPaymentHistory: (id) => api.get(`/buyer/invoices/${id}/payments`),
  getInvoiceStats: () => api.get('/buyer/invoices/stats'),

  // Seller
  createInvoice: (data) => api.post('/seller/invoices', data),
  uploadInvoice: (formData) => api.post('/seller/invoices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateInvoice: (id, data) => api.put(`/seller/invoices/${id}`, data),
  updateInvoiceStatus: (id, data) => api.put(`/seller/invoices/${id}/status`, data),
  sendInvoiceEmail: (id, data) => api.post(`/seller/invoices/${id}/send`, data),

  // Admin
  getAllInvoices: () => api.get('/admin/invoices'),
  getInvoiceByEnquiry: (enquiryId) => api.get(`/admin/invoices/enquiry/${enquiryId}`),
  deleteInvoice: (id) => api.delete(`/admin/invoices/${id}`),
  sendInvoiceToBuyer: (invoiceId) => api.post(`/admin/invoices/${invoiceId}/send`),
  getInvoiceReports: (params = {}) => api.get('/admin/invoices/reports', { params }),
};

// ==================== SETTINGS API ====================
export const settingsAPI = {
  getCountries: () => api.get('/settings/countries'),
  getCurrencies: () => api.get('/settings/currencies'),
  getUnits: () => api.get('/settings/units'),
  getPaymentMethods: () => api.get('/settings/payment-methods'),
  getShippingMethods: () => api.get('/settings/shipping-methods'),
  getInvoiceSettings: () => api.get('/settings/invoice'),
  updateInvoiceSettings: (data) => api.put('/settings/invoice', data),
};

// ==================== NOTIFICATION API ====================
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
  deleteAll: () => api.delete('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
};

// ==================== ANALYTICS API ====================
export const analyticsAPI = {
  getDashboardAnalytics: () => api.get('/analytics/dashboard'),
  getSalesAnalytics: (params = {}) => api.get('/analytics/sales', { params }),
  getUserAnalytics: () => api.get('/analytics/users'),
  getProductAnalytics: () => api.get('/analytics/products'),
  getInvoiceAnalytics: () => api.get('/analytics/invoices'),
};

// ==================== UTILITY FUNCTIONS ====================
export const apiHelper = {
  createFormData: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          formData.append(key, value.length > 0 && typeof value[0] === 'object' ? JSON.stringify(value) : value.join(','));
        } else if (value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });
    return formData;
  },
  formatPrice: (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  },
  formatDate: (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  },
  formatDateTime: (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  },
  formatNumber: (number) => {
    if (!number && number !== 0) return '0';
    return new Intl.NumberFormat('en-US').format(number);
  },
  generateInvoiceNumber: () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${timestamp}${random}`;
  },
  calculateInvoiceTotal: (items, taxRate = 0, discount = 0) => {
    if (!items || !Array.isArray(items)) return 0;
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0), 0);
    const tax = subtotal * (parseFloat(taxRate) || 0) / 100;
    const discountAmount = subtotal * (parseFloat(discount) || 0) / 100;
    return subtotal + tax - discountAmount;
  },
};

// ==================== AUTH UTILITIES ====================
export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  if (!token || !user) return false;
  try {
    const userData = JSON.parse(user);
    return !!(token && userData.id);
  } catch { return false; }
};

export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch { return null; }
};

export const getUserType = () => { const user = getCurrentUser(); return user ? user.user_type : null; };
export const isAdmin   = () => getUserType() === 'admin';
export const isBuyer   = () => getUserType() === 'buyer';
export const isSeller  = () => getUserType() === 'seller';

export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('refreshToken');
};

export const setAuthData = (token, user) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
};

export const downloadFile = async (endpoint, filename) => {
  const response = await api.get(endpoint, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const handleApiError = (error) => {
  if (error.isNetworkError) return { success: false, message: error.message, error: 'NETWORK_ERROR' };
  if (error.response) {
    const { data, status } = error.response;
    return { success: false, message: data.message || `Server error (${status})`, error: data.error || 'SERVER_ERROR', status, data };
  }
  return { success: false, message: 'An unexpected error occurred', error: 'UNKNOWN_ERROR' };
};

export const handleApiSuccess = (response) => {
  if (response.data && typeof response.data === 'object') return { success: true, ...response.data };
  return { success: true, data: response.data, message: 'Operation successful' };
};

export default api;